import { Grid, Theme } from "@material-ui/core";
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, EditorState, convertToRaw, DefaultDraftBlockRenderMap, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';

const toolbar = {
  options: ['inline', 'list', 'textAlign'],
  inline: {
    inDropdown: false,
    options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
  },
  list: {
    inDropdown: false,
    options: ['unordered', 'ordered', 'indent', 'outdent'],
  },
  textAlign: {
    inDropdown: false,
    options: ['left', 'center', 'right', 'justify'],
  },
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    richTextWrapper: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%'
    },
    hiddenToolbar: {
      display: 'none',
    },
    richTextStyles: {
      padding: theme.spacing(0, 2),
      width: '100%',
      '& .public-DraftStyleDefault-block': {
        margin: 'auto'
      },
      '& .DraftEditor-editorContainer, .DraftEditor-root, .public-DraftEditor-content': {
        height: 'auto'
      },
    },
    templateInput: {
      boxShadow: 'inset 0 0 6px rgba(219, 219, 219, 0.5)',
      borderBottomRightRadius: '10px',
      borderBottomLeftRadius: '10px',
      marginBottom: '10px',
    },
    richTextContent: {
      height: '350px',
    },
  })
);

const PrescreenRichText = observer(({
  onChange,
  value,
  readOnly = false,
  reloadDependencies = [] }:
  {
    onChange: (value: string) => void,
    value: string,
    readOnly?: boolean,
    reloadDependencies?: any[]
  }) => {

  const classes = useStyles();
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const getSafeBodyFromHTML = (html: string): Element => {
    return new DOMParser().parseFromString(html, 'text/html').body;
  }

  useEffect(() => {
    if (value) updateEditorState(value);
  }, reloadDependencies);

  const normalizeHtmlForDraft = (html: string): string => {
    return html
      .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p>')
      .replace(/<u>(.*?)<br\s*\/?>\s*<\/u>/gi, '<u>$1</u></p><p>')
      .replace(/<br\s*\/?>\s*(\(?[A-Z])/g, '</p><p>$1')
      .replace(/<br\s*\/?>\s*(https?:\/\/[^\s<]+)/gi, '</p><p>$1')
      .replace(/<p>\s*<\/p>/gi, '<br>')
      .replace(/<br\s*\/?><\/p>/gi, '</p>')
      .replace(/<ins>/gi, '<u>').replace(/<\/ins>/gi, '</u>')
      .replace(/\s+<\/p>/gi, '</p>').replace(/<p>\s+/gi, '<p>')
  };

  const updateEditorState = (value: string) => {
    const normalizedHtml = normalizeHtmlForDraft(value);
    const blockRenderMap = DefaultDraftBlockRenderMap.set('br', { element: 'br' });
    const blocksFromHTML = convertFromHTML(normalizedHtml, getSafeBodyFromHTML, blockRenderMap);
    const state = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
    setEditorState(EditorState.createWithContent(state));
  };

  const handleEditorStateChange = (newState) => {
    setEditorState(newState);
    onChange(draftToHtml(convertToRaw(newState.getCurrentContent())));
  }

  return (
    <Grid container className={classes.templateInput}>
      <Editor
        readOnly={readOnly}
        toolbar={toolbar}
        toolbarClassName={readOnly ? classes.hiddenToolbar : null}
        editorState={editorState}
        editorClassName={`${classes.richTextStyles} ${classes.richTextContent}`}
        wrapperClassName={classes.richTextWrapper}
        onEditorStateChange={handleEditorStateChange}
        spellCheck
      />
    </Grid>)
});

export default PrescreenRichText;